// Libraries
import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, inject, OnDestroy, OnInit, signal, Signal, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { GoogleMap } from '@angular/google-maps';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';

// Interfaces
import { CalibrationMode, CalibrationPoint, CalibrationResult } from 'src/app/interfaces/shared/calibration';

// Classes
import { ImageCalibration } from 'src/app/classes/maps/ImageCalibration';
import { Pop } from 'src/app/classes/shared/pop';

// Components
import { MapStore } from 'src/app/services/stores/map.store';
import { ButtonComponent } from 'ui-library';

export interface ImageCalibrationDialogData {
    imageUrl: string;
    imageWidth: number;
    imageHeight: number;
    currentMapCoord: {
        north: number;
        south: number;
        east: number;
        west: number;
    };
    googleMap: GoogleMap;
}

export interface CalibrationToolAPI {
    enable(): Promise<void>;
    destroy(): void;
    undo?(): void;
    reset?(): void;
    getResult?(): CalibrationResult | null;
}

@Component({
    selector: 'app-calibration',
    templateUrl: './calibration.component.html',
    styleUrls: ['./calibration.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDialogModule,
        MatToolbarModule,
        MatButtonModule,
        MatIconModule,
        MatCardModule,
        MatFormFieldModule,
        MatInputModule,
        TranslateModule,
        ButtonComponent,
    ],
})

export class CalibrationComponent implements OnInit, AfterViewInit, OnDestroy {
    // Services
    private readonly fb: FormBuilder = inject(FormBuilder);
    private readonly translate: TranslateService = inject(TranslateService);
    private readonly pop: Pop = inject(Pop);
    private readonly mapStore: MapStore = inject(MapStore);

    // Components API
    @ViewChild(GoogleMap) googleMap!: GoogleMap;
    @ViewChild('calibrationMapContainer') calibrationMapContainer!: ElementRef;

    // Signals
    private readonly defaultMode: Signal<CalibrationMode> = signal('idle');
    private readonly defaultPoint: Signal<CalibrationPoint | null> = signal(null);
    private readonly defaultResult: Signal<CalibrationResult | null> = signal(null);
    private readonly defaultError: Signal<string | null> = signal(null);
    public readonly imageInfo: Signal<{ width: number; height: number } | null> = this.mapStore.imageInfo;

    // Variables
    private calibrationTool: ImageCalibration | null = null;
    private destroy$ = new Subject<void>();

    point1Form!: FormGroup;
    point2Form!: FormGroup;

    isLoading: boolean = false;
    showPreview: boolean = false;

    // Getters
    get calibrationMode() {
        return this.calibrationTool?.mode ?? this.defaultMode;
    }

    get calibrationPoint1() {
        return this.calibrationTool?.point1 ?? this.defaultPoint;
    }

    get calibrationPoint2() {
        return this.calibrationTool?.point2 ?? this.defaultPoint;
    }

    get calibrationPreview() {
        return this.calibrationTool?.previewResult ?? this.defaultResult;
    }

    get calibrationError() {
        return this.calibrationTool?.error ?? this.defaultError;
    }



    ngOnInit(): void {
        this.initForms();
    }

    ngAfterViewInit(): void {
        this.initializeCalibration();
    }

    ngOnDestroy(): void {
        if (this.calibrationTool) {
            this.calibrationTool.destroy();
        }

        this.destroy$.next();
        this.destroy$.complete();
    }
    private initForms(): void {
        this.point1Form = this.fb.group({
            lat: [0, [Validators.required, Validators.min(-90), Validators.max(90)]],
            lon: [0, [Validators.required, Validators.min(-180), Validators.max(180)]],
        });

        this.point2Form = this.fb.group({
            lat: [0, [Validators.required, Validators.min(-90), Validators.max(90)]],
            lon: [0, [Validators.required, Validators.min(-180), Validators.max(180)]],
        });
    }

    private async initializeCalibration(): Promise<void> {
        try {
            this.isLoading = true;

            const model = this.mapStore.currentModel();
            const map = this.mapStore.map();

            if (!model || !map) return;

            this.calibrationTool = new ImageCalibration({
                map: map,
                imageUrl: model.mapImageSrc || model.mapImage,
                imageWidth: this.imageInfo()?.width || 0,
                imageHeight: this.imageInfo()?.height || 0,
                currentMapCoord: model.mapCoord || { north: 0, south: 0, east: 0, west: 0 },
                containerElement: this.calibrationMapContainer.nativeElement,
                onComplete: (result) => this.onCalibrationComplete(result),
                onCancel: () => this.onCalibrationCancel(),
            });

            await this.calibrationTool.enable();
            this.isLoading = false;

        } catch (error) {
            console.error('Erreur initialization calibrage:', error);
            this.pop.toast('Erreur lors de l\'initialisation', 'danger', 4000);
        }
    }

    onEditPoint(point: 'point1' | 'point2'): void {
        if (!this.calibrationTool) return;
        this.calibrationTool.setEditingPoint(point);
    }

    get calibrationEditingPoint() {
        return this.calibrationTool?.editingPoint ?? signal(null);
    }

    cancelCalibration(): void {
        this.mapStore.setEditorMode('none');
    }

    onCalculate(): void {
        if (!this.point1Form.valid || !this.point2Form.valid || !this.calibrationTool) return;

        const { lat: lat1, lon: lon1 } = this.point1Form.value;
        const { lat: lat2, lon: lon2 } = this.point2Form.value;

        this.calibrationTool.calculateFromCoords(lat1, lon1, lat2, lon2);
        this.calibrationTool.confirm();
    }

    private onCalibrationComplete(result: CalibrationResult): void {
        this.mapStore.saveCalibration(result);
    }

    private onCalibrationCancel(): void {
    }

    getErrorMessage(error: string | null): string {
        return error ? this.translate.instant(error) : '';
    }
}